import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Gem, Compass, Layers } from "lucide-react";
import { GROWTH_FACTOR_URL } from "../data/config";
import { Reveal } from "../components/common/Reveal";
import { track } from "../lib/track";

const GF_DETAIL = [
  {
    id: "personal",
    label: "Personal Growth",
    sub: "Emerald + Gold",
    icon: Gem,
    theme: "bg-emerald",
    features: [
      "Reflective assessment",
      "Personalised premium report",
      "Growth Snapshot",
      "Gemstone symbolism & colour reflection",
      "Action direction",
    ],
  },
  {
    id: "business",
    label: "Business Growth",
    sub: "Navy + Gold",
    icon: Compass,
    theme: "bg-navy",
    features: [
      "Business context + diagnostic",
      "Business Bird Archetype",
      "Bottleneck identification",
      "Growth priorities",
      "90-day direction",
      "Growth Snapshot",
    ],
  },
  {
    id: "organisation",
    label: "Organisation / Cohort",
    sub: "Royal Purple + Gold",
    icon: Layers,
    theme: "bg-royal",
    features: [
      "GF Access Code",
      "Before assessment",
      "Programme / intervention",
      "After assessment",
      "Aggregate cohort impact",
      "Consultant interpretation",
      "Organisation report",
    ],
  },
];

export default function GrowthFactorPage() {
  return (
    <main data-testid="growth-factor-page">
      <section className="bg-cream py-24 lg:py-32" data-testid="gf-hero">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow">Growth Factor</p>
            <h1 className="mt-4 max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-6xl">
              BEFORE GROWTH COMES <span className="italic text-emerald">DIAGNOSIS.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink/75 sm:text-lg">
              Growth Factor is a proprietary developmental diagnostic ecosystem,
              created to help individuals, businesses and organisations gain
              clarity before taking action.
            </p>
            <a
              href={GROWTH_FACTOR_URL}
              target="_blank"
              rel="noreferrer"
              className="btn-primary mt-10"
              data-testid="gf-start-cta"
              onClick={() => track("growthfactor_checkout_started", { source: "growth-factor-page", campaign: "growth-factor", cta_position: "hero" })}
            >
              Start Growth Factor <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
            </a>
            <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/50" data-testid="gf-hero-price">
              Full diagnostic report — <span className="text-emerald">RM15</span>
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28" data-testid="gf-pathways">
        <div className="container-site">
          <div className="grid gap-6 lg:grid-cols-3">
            {GF_DETAIL.map((g, i) => {
              const Icon = g.icon;
              return (
                <Reveal key={g.id} delay={i * 0.1} className="h-full">
                  <article
                    className={`flex h-full flex-col ${g.theme} p-8 text-cream`}
                    data-testid={`gf-detail-${g.id}`}
                  >
                    <div className="h-0.5 w-10 bg-gold" aria-hidden="true" />
                    <Icon className="mt-8 h-7 w-7 text-gold" strokeWidth={1.25} aria-hidden="true" />
                    <h2 className="mt-6 font-serif text-2xl tracking-tight">{g.label}</h2>
                    <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-gold">
                      {g.sub}
                    </p>
                    <ul className="mt-8 flex-1 space-y-3">
                      {g.features.map((f) => (
                        <li key={f} className="flex items-start gap-3 text-sm leading-relaxed text-cream/80">
                          <span className="mt-2 block h-px w-4 shrink-0 bg-gold" aria-hidden="true" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    {g.id !== "organisation" && (
                      <p className="mt-8 text-[10px] font-semibold uppercase tracking-[0.2em] text-cream/60" data-testid={`gf-price-${g.id}`}>
                        Full report — RM15
                      </p>
                    )}
                    <a
                      href={GROWTH_FACTOR_URL}
                      target="_blank"
                      rel="noreferrer"
                      className={`${g.id === "organisation" ? "mt-10" : "mt-3"} inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:text-goldbright`}
                      data-testid={`gf-detail-cta-${g.id}`}
                      onClick={() => track("growthfactor_checkout_started", { source: "growth-factor-page", campaign: "growth-factor", cta_position: `pathway-${g.id}` })}
                    >
                      Begin <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                    </a>
                  </article>
                </Reveal>
              );
            })}
          </div>
          <p
            className="mt-12 border-l-2 border-gold pl-6 font-serif text-lg italic leading-relaxed text-navy"
            data-testid="gf-privacy-note"
          >
            Privacy, by design: the organisation receives aggregate insight, not
            private individual disclosures.
          </p>
        </div>
      </section>

      <section className="bg-sand py-20 lg:py-28" data-testid="gf-organisations">
        <div className="container-site grid items-center gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-8">
            <p className="eyebrow">For organisations</p>
            <h2 className="mt-4 font-serif text-3xl tracking-tight text-navy sm:text-4xl">
              Measure your cohort <span className="italic text-emerald">before and after.</span>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/75">
              Run Growth Factor around your programme or intervention with a GF
              Access Code — a Before assessment, your programme, an After
              assessment, and an aggregate cohort report interpreted with our
              consultants.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4">
            <Link to="/contact" className="btn-primary" data-testid="gf-cohort-cta">
              Request a Cohort / Programme Discussion <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
